/** @jsxImportSource @emotion/react */

import type { ProjectStateLifecycle } from '@/Client/Layout';
import { formatUsd } from '@/Client/Presenters';
import { useScanEditingContextSelector } from '@/Client/State';
import {
  getAllScopedTasks,
  getAllShownOnScopeReportGroups,
  getAllShownOnScopeReportTasks,
  type ScanGroupOptimized,
} from '@kaiizen/kai-scan-template-system';

import { MetaList } from '@/Client/Widgets/MetaList';
import { pluralize } from '@art-suite/art-core-ts';
import { getFullScopeOverviewTotalCost, getGroupOverviewReviewedTasks } from './focusedGroupOverviewLib';
import {
  OVERVIEW_HEADER_AMOUNT_STYLE,
  OVERVIEW_HEADER_BOX_STYLE,
  OVERVIEW_HEADER_TITLE_BLOCK_STYLE,
  OVERVIEW_HEADER_TITLE_ROW_STYLE,
  OVERVIEW_HEADER_TITLE_STYLE,
} from './focusedOverviewHeaderStyles';

export const FULL_SCOPE_OVERVIEW_TITLE = 'Scope';

/**
 * The whole-scope overview box, per the ShopEdit prototype's scope page: "Scope"
 * over a count line, the total right.
 */
export const FocusedFullScopeOverviewHeader = ({
  groups,
  projectStateLifecycle,
}: {
  groups: readonly ScanGroupOptimized[];
  projectStateLifecycle: ProjectStateLifecycle;
}) => {
  const {
    approvalStatusByTaskId,
    scopedGroups,
    scopedTaskCount,
    scopedTasks,
  } = useScanEditingContextSelector(({ scanRoot, approvalStatusByTaskId }) => {
    const scopedTasks = scanRoot ? getAllScopedTasks(scanRoot) : [];
    const scopedGroups = scanRoot ? getAllShownOnScopeReportGroups(scanRoot) : [];
    const scopedTaskCount = scanRoot ? getAllShownOnScopeReportTasks(scanRoot).length : 0;
    return ({
      scopedGroups,
      scopedTaskCount,
      approvalStatusByTaskId,
      scopedTasks,
    });
  });

  const reviewedCount = getGroupOverviewReviewedTasks(scopedTasks, approvalStatusByTaskId).length;

  return (
    <div css={OVERVIEW_HEADER_BOX_STYLE} data-testid='focused-full-scope-overview-header'>
      <div css={OVERVIEW_HEADER_TITLE_ROW_STYLE}>
        <div css={OVERVIEW_HEADER_TITLE_BLOCK_STYLE}>
          <h2 css={OVERVIEW_HEADER_TITLE_STYLE}>{FULL_SCOPE_OVERVIEW_TITLE}</h2>
          <MetaList
            parts={[
              pluralize(scopedTaskCount, 'task'),
              pluralize(scopedGroups.length, 'group'),
              ...projectStateLifecycle === 'review' ? [`${reviewedCount} reviewed`] : [],
            ]}
          />
        </div>
        <span css={OVERVIEW_HEADER_AMOUNT_STYLE}>
          {formatUsd(getFullScopeOverviewTotalCost(groups), false, true)}
        </span>
      </div>
    </div>
  );
};
